export const sumOfArray = (arr: number[]): number =>
  arr.reduce((c, n) => c + n, 0);

export const averageOfArray = (arr: number[]): number =>
  sumOfArray(arr) / arr.length;

export interface LinearRegressionProps {
  x: number[];
  y: number[];
  [key: string]: any;
}

export const linearRegression = (
  x: number[],
  ys: number[]
): LinearRegressionProps => {
  let xs = x;
  if (!xs.length) {
    ys.forEach((e, i) => {
      xs.push(i + 1);
    });
  }
  const xMean = averageOfArray(xs);
  const yMean = averageOfArray(ys.filter((e) => !Number.isNaN(e)));
  let dividend = 0;
  let divisor = 0;
  for (let i = 0; i < xs.length; ++i) {
    dividend += (xs[i] - xMean) * (ys[i] - yMean);
    divisor += (xs[i] - xMean) * (xs[i] - xMean);
  }
  const slope = dividend / divisor;
  const intercept = yMean - xMean * slope;
  const result: number[] = [];
  xs.forEach((e) => {
    result.push(e * slope + intercept);
  });
  const y_to_yMean = ys.map((e) => (e - yMean) * (e - yMean));
  const y_linear_to_yMean = result.map((e) => (e - yMean) * (e - yMean));
  const r = sumOfArray(y_linear_to_yMean) / sumOfArray(y_to_yMean);
  const n = Math.pow(Math.E, (-1 * intercept) / slope);
  return {
    slope: slope,
    y: [intercept].concat(result),
    x: [0].concat(xs),
    error: r,
    intercept: intercept,
    n: n,
  };
};

export interface WeibullProps {
  x: number[];
  y: number[];
  f: number[];
  [key: string]: any;
}

export const getWeibullScale = (
  scale = [
    0.0001, 0.0005, 0.005, 0.01, 0.05, 0.1, 0.2, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95,
    0.99,
  ]
): { tickvals: number[]; ticktext: string[] } => {
  const val = scale.map((e) => Math.log(Math.log(1 / (1 - e))));
  const text = scale.map((e) => `${e * 100}%`);

  return { tickvals: val, ticktext: text };
};

export const weibullDistribution = (arr: number[]): WeibullProps => {
  arr.sort((p, n) => (p > n ? 1 : -1));
  const f = arr.map((e, i) => (i + 0.5) / arr.length);
  // const x = arr.map((e) => Math.log(e));
  const y = f.map((e) => Math.log(Math.log(1 / (1 - e))));

  return { x: arr, y: y, f: f, n: arr.length };
};

export const unit: { [key: string]: { unit: string; name: string } } = {
  RTH: {
    unit: "<sup>K</sup>/<sub>W</sub>",
    name: "R<sub>TH</sub>",
  },
  VF_ANF: { unit: "V", name: "V<sub>F_START</sub>" },
  VF_END: { unit: "V", name: "V<sub>F_END</sub>" },
  TVM_0: { unit: "K", name: "T<sub>VM_0</sub>" },
  DELTA_TVM: { unit: "K", name: "ΔT<sub>VM</sub>" },
  TVM_ANF: { unit: "K", name: "T<sub>VM_START</sub>" },
  TVM_END: { unit: "K", name: "T<sub>VM_END</sub>" },
};

export const getXIntersections = (lg: any, value = 0.000000000000001) => {
  if (!lg) {
    return 0;
  }

  return (Math.log(Math.log(1 / (1 - value))) - lg.intercept) / lg.slope;
};
